'use client'

import { createClient } from "@/lib/supabase/client"
import { checkServerPermission } from "@/lib/user/server-permission"

export interface Notification {
    id: string
    message: string
    type: string
    payload: any
    status: 'unresolved' | 'resolved'
    created_at: string
    updated_at: string
}

// Get all unresolved notifications for admin
export const getUnresolvedNotifications = async (): Promise<Notification[]> => {
    const supabase = await createClient()
    
    const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .neq('status', 'resolved')
        .order('created_at', { ascending: false })
    
    if (error) {
        console.error("Failed to fetch notifications:", error)
        return []
    }
    
    return data || []
}

// Mark notification as resolved
export const resolveNotification = async (id: string): Promise<boolean> => {
    const { isAllowed } = await checkServerPermission("is_admin")
    if (!isAllowed) return false

    const supabase = await createClient()
    
    const { error } = await supabase
        .from('notifications')
        .update({ status: 'resolved', updated_at: new Date().toISOString() })
        .eq('id', id)
    
    if (error) {
        console.error("Failed to resolve notification:", error)
        return false
    }
    
    return true
}

// Create a new notification
export const createNotification = async (notification: Omit<Notification, 'id' | 'created_at' | 'updated_at' | 'status'>): Promise<Notification | null> => {
    const { isAllowed } = await checkServerPermission("is_admin")
    if (!isAllowed) return null

    const supabase = await createClient()
    
    const { data, error } = await supabase
        .from('notifications')
        .insert({
            ...notification,
            status: 'unresolved'
        })
        .select()
        .single()
    
    if (error) {
        console.error("Failed to create notification:", error)
        return null
    }
    
    return data
} 